/**
 * Receipt Analysis Service
 * Handles image analysis using AI to extract bill items and amounts
 */
// URL de Supabase (se obtiene de las variables de entorno)
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Analyzes a receipt image and extracts items with prices
 * Usa la Edge Function de Supabase como proxy seguro para OpenAI
 * @param {string} imageBase64 - Base64 encoded image
 * @returns {Promise<Object>} - Parsed receipt data
 */
export const analyzeReceipt = async (imageBase64) => {
  try {
    // Llamar a la Edge Function de Supabase
    const response = await fetch(`${SUPABASE_URL}/functions/v1/analyze-receipt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY
      },
      body: JSON.stringify({ imageBase64 })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al analizar el ticket');
    }

    const result = await response.json();

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Respuesta inválida del servidor');
    }

    const receiptData = result.data;

    // Validate response structure
    if (!receiptData.items || !Array.isArray(receiptData.items)) {
      throw new Error('Formato de respuesta inválido');
    }

    // Generate a session ID based on restaurant and timestamp
    const sessionId = generateSessionId(receiptData.restaurant, receiptData.date);

    return {
      success: true,
      sessionId,
      data: receiptData
    };

  } catch (error) {
    console.error('Error analyzing receipt:', error);
    return {
      success: false,
      error: error.message || 'Error al analizar el ticket'
    };
  }
};

/**
 * Analyzes a receipt using a mock/demo mode for testing without API key
 * @param {string} imageBase64 - Base64 encoded image (unused in mock)
 * @returns {Promise<Object>} - Mock receipt data
 */
export const analyzeReceiptMock = async (imageBase64) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 2000));

  const mockData = {
    restaurant: 'Restaurante La Tasca',
    date: new Date().toISOString(),
    items: [
      { name: 'Ensalada César', quantity: 2, unitPrice: 8.50, totalPrice: 17.00 },
      { name: 'Pasta Carbonara', quantity: 1, unitPrice: 12.90, totalPrice: 12.90 },
      { name: 'Pizza Margarita', quantity: 1, unitPrice: 11.50, totalPrice: 11.50 },
      { name: 'Cerveza 33cl', quantity: 3, unitPrice: 2.80, totalPrice: 8.40 },
      { name: 'Agua 1L', quantity: 2, unitPrice: 2.00, totalPrice: 4.00 },
      { name: 'Tiramisú', quantity: 1, unitPrice: 5.50, totalPrice: 5.50 },
      { name: 'Café Espresso', quantity: 2, unitPrice: 1.50, totalPrice: 3.00 }
    ],
    subtotal: 62.30,
    tax: 6.23,
    tip: 0,
    total: 68.53
  };

  const sessionId = generateSessionId(mockData.restaurant, mockData.date);

  return {
    success: true,
    sessionId,
    data: mockData
  };
};

/**
 * Generates a unique session ID for a receipt
 * @param {string} restaurant - Restaurant name
 * @param {string} date - Receipt date
 * @returns {string} - Session ID
 */
const generateSessionId = (restaurant = 'UNKNOWN', date = null) => {
  const restaurantName = restaurant || 'UNKNOWN';
  const restaurantSlug = restaurantName
    .replace(/[^a-zA-Z0-9]/g, '-')
    .substring(0, 15)
    .toUpperCase();

  // Usar timestamp completo con milisegundos para garantizar unicidad
  const timestamp = Date.now();
  const dateObj = new Date(timestamp);
  const dateStr = dateObj.toISOString().split('T')[0];
  const timeStr = dateObj.toTimeString().substring(0, 8).replace(/:/g, '');
  const ms = dateObj.getMilliseconds().toString().padStart(3, '0');

  return `${restaurantSlug}-${dateStr}-${timeStr}-${ms}`;
};

/**
 * Converts an image file to base64
 * @param {File} file - Image file
 * @returns {Promise<string>} - Base64 encoded image
 */
export const imageToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Captures image from video stream
 * @param {HTMLVideoElement} videoElement - Video element
 * @returns {string} - Base64 encoded image
 */
export const captureImageFromVideo = (videoElement) => {
  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoElement, 0, 0);

  return canvas.toDataURL('image/jpeg', 0.8);
};
